import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChevronLeft, ContactRound, Trash2, Car, ExternalLink } from "lucide-react";
import AnagraficaDatiForm, { validateCfPivaObbligatori } from "@/components/anagrafiche/AnagraficaDatiForm";
import { toast } from "sonner";
import { useDirtyGuard } from "@/hooks/useDirtyGuard";
import { AuditRowAuto } from "@/components/AuditRows";
import {
  CATEGORIE_LABELS, TIPO_PERSONA_LABELS, summarizeCategorie,
} from "@/lib/anagrafiche";
import CategorieEditor from "@/components/anagrafiche/CategorieEditor";
import DocumentsTab from "@/components/DocumentsTab";
import { useDoubleConfirm } from "@/components/ConfirmProvider";
import { useAuth, isElevated } from "@/contexts/AuthContext";
import CodiceIvaPicker from "@/components/CodiceIvaPicker";
import Conflitti, { campiModificati, sovrascrittura } from "@/components/Conflitti";

const NON_INVIATI = ["id", "codice", "created_at", "created_by", "updated_at", "deleted_at",
  "deleted_by", "delete_reason", "_migrated_from", "_warnings"];
const pulisci = (x) => {
  const p = { ...(x || {}) };
  NON_INVIATI.forEach((k) => delete p[k]);
  return p;
};
// Sprint CRM-2C — cosa si manda al salvataggio: solo i campi cambiati, con i valori al caricamento.
// Sprint CRM-2C.1 — i campi svuotati NON si tolgono più: campiModificati li manda come vuoto (null).
export const datiDaSalvare = (iniziale, a) => campiModificati(pulisci(iniziale), pulisci(a));

export default function AnagraficaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const confirm = useDoubleConfirm();
  const { user } = useAuth();
  const elev = isElevated(user);
  const [a, setA] = useState(null);
  // Sprint CRM-2C — punto di partenza (al caricamento) e finestra comune dei conflitti
  const [iniziale, setIniziale] = useState(null);
  const [esitoConflitti, setEsitoConflitti] = useState(null);
  const [mieiValori, setMieiValori] = useState(null);
  const [veicoli, setVeicoli] = useState([]);
  const [audit, setAudit] = useState([]);
  const [taxonomy, setTaxonomy] = useState({});
  const [saving, setSaving] = useState(false);
  // Sprint 4A.6: dirty guard
  const [isDirty, setIsDirty] = useState(false);
  // Sprint 4D (C2)
  const [dupModal, setDupModal] = useState({ open: false, existing: null, pendingPayload: null });
  // Sprint 4D (C6) — campi accesso login (form-only, NON persisted in anagrafica)
  // Sprint UTENTI-2A: rimosso state accessForm (legame anagrafica→utente smontato).

  const load = async () => {
    try {
      const r = await api.get(`/anagrafiche/${id}`);
      setA(r.data);
      setIniziale(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    api.get(`/anagrafiche/${id}/veicoli`).then((r) => setVeicoli(r.data?.items || [])).catch(() => {});
    api.get(`/audit-log?entity_type=anagrafica&entity_id=${id}`)
      .then((r) => setAudit(r.data?.items || r.data || [])).catch(() => {});
    api.get("/anagrafiche/_meta/categorie").then((r) => setTaxonomy(r.data || {})).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Sprint UTENTI-2A (2026-05-31): canManageAccess RIMOSSO. Gli utenti si
  // gestiscono SOLO da Impostazioni > Utenti.

  // Sprint 4D.1 (E1): warnings dal backend (es. CF incoerente)
  const [warnings, setWarnings] = useState([]);

  const set = (patch) => { setA({ ...a, ...patch }); setIsDirty(true); };

  // Sprint 4D.2: requiresTelefono/showAccessSection sono calcolati internamente
  // al componente AnagraficaDatiForm (logica duplicata rimossa qui).

  const save = async (force = false) => {
    if (!a) return;
    const fiscalErr = validateCfPivaObbligatori(a);
    if (fiscalErr) { toast.error(fiscalErr); return; }
    setSaving(true);
    try {
      // Sprint UTENTI-2A: rimossa logica access_login_* (legame anagrafica→utente smontato).
      const url = force ? `/anagrafiche/${id}?force_duplicate_cf=true` : `/anagrafiche/${id}`;
      // Sprint CRM-2C — solo i campi cambiati, ciascuno con il valore che aveva al caricamento
      const { payload: diff, originali } = datiDaSalvare(iniziale, a);
      const r = await api.patch(url, { ...diff, originali });
      // Sprint 4D.1 (E1): leggi warnings dal response
      setWarnings(Array.isArray(r.data?._warnings) ? r.data._warnings : []);
      if (r.data?.conflitti?.length) { setMieiValori(diff); setEsitoConflitti(r.data); }
      else toast.success("Salvato");
      setIsDirty(false);
      setDupModal({ open: false, existing: null, pendingPayload: null });
      load();
    } catch (e) {
      const status = e?.response?.status;
      const det = e?.response?.data?.detail;
      if (status === 409 && det?.error === "duplicate_codice_fiscale") {
        setDupModal({ open: true, existing: det.existing_anagrafica });
      } else {
        toast.error(typeof det === "string" ? det : (det?.message || "Errore"));
      }
    } finally { setSaving(false); }
  };

  const applicaConflitti = async (scelte) => {
    const miei = esitoConflitti.conflitti.filter((c) => scelte[c.campo] === "mio");
    setEsitoConflitti(null);
    if (!miei.length) { toast.success("Tenuti i valori attuali"); return; }
    const { payload, originali } = sovrascrittura(miei, mieiValori);
    try {
      const r = await api.patch(`/anagrafiche/${id}`, { ...payload, originali });
      if (r.data?.conflitti?.length) setEsitoConflitti(r.data);
      else toast.success(`Sovrascritt${miei.length === 1 ? "o 1 campo" : `i ${miei.length} campi`}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // Sprint UTENTI-2A: funzione resetPassword (su anagrafica) RIMOSSA.
  // Il reset password si fa da Impostazioni > Utenti.

  // Sprint 4A.6: dirty guard modal (base URL = /anagrafiche/{id})
  const { GuardModal } = useDirtyGuard({
    isDirty,
    onSave: save,
    baseUrl: id ? `/anagrafiche/${id}` : undefined,
  });

  const remove = async () => {
    const ok = await confirm({
      variant: "delete", title: "Elimina anagrafica",
      message: `Stai per eliminare "${labelOf(a)}". Sarà spostata nel cestino.`,
      confirmLabel: "SI, elimina",
    });
    if (!ok) return;
    const motivo = window.prompt("Motivo dell'eliminazione (obbligatorio, min 5 caratteri):") || "";
    if (motivo.trim().length < 5) { toast.error("Motivo obbligatorio (min 5)"); return; }
    try {
      await api.delete(`/anagrafiche/${id}`, { data: { motivo } });
      toast.success("Anagrafica eliminata");
      nav("/anagrafiche");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const labelOf = (a) =>
    a?.ragione_sociale || `${a?.cognome || ""} ${a?.nome || ""}`.trim() || a?.codice || "";

  if (!a) return <div className="text-slate-500">Caricamento...</div>;
  const sum = summarizeCategorie(a.categorie);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/anagrafiche" className="text-slate-500 hover:text-slate-700"><ChevronLeft className="h-5 w-5" /></Link>
          <ContactRound className="h-6 w-6 text-blue-600" />
          <div>
            <div className="text-xs font-mono text-slate-500">{a.codice}</div>
            <h1 className="font-heading text-2xl font-semibold">{labelOf(a)}</h1>
            <div className="flex flex-wrap gap-1 mt-1 items-center">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-medium">
                {TIPO_PERSONA_LABELS[a.tipo_persona] || a.tipo_persona}
              </span>
              {sum.slice(0, 6).map((c, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100"
                      title={c.group}>
                  {c.label}
                </span>
              ))}
              {sum.length > 6 && (
                <span className="text-[10px] text-slate-400">+{sum.length - 6}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={remove} className="text-rose-700 border-rose-200 hover:bg-rose-50"
                  data-testid="anag-delete-btn">
            <Trash2 className="h-4 w-4 mr-1.5" /> Elimina
          </Button>
          <Button onClick={() => save(false)} disabled={saving} className="bg-blue-600 hover:bg-blue-700"
                  data-testid="anag-save-btn">
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="dati">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="dati">Dati anagrafici</TabsTrigger>
          <TabsTrigger value="economici">Dati economici</TabsTrigger>
          <TabsTrigger value="fatt_e">Fatt. elettronica</TabsTrigger>
          <TabsTrigger value="documenti">Documenti</TabsTrigger>
          <TabsTrigger value="veicoli">Veicoli associati ({veicoli.length})</TabsTrigger>
          <TabsTrigger value="pratiche">Pratiche</TabsTrigger>
          {elev && <TabsTrigger value="storico">Storico</TabsTrigger>}
        </TabsList>

        {/* Sprint 4D.1 (E1): banner warning CF incoerente - ora gestito dentro AnagraficaDatiForm */}

        <TabsContent value="dati" className="mt-4">
          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <AnagraficaDatiForm
              value={a}
              onChange={(next) => { setA(next); setIsDirty(true); }}
              taxonomy={taxonomy}
              mode="edit"
              warnings={warnings}
            />
          </Card>
        </TabsContent>


        <TabsContent value="economici" className="mt-4">
          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-3">
                <Label className="text-xs">Codice IVA</Label>
                <CodiceIvaPicker
                  value={a.iva_codice || ""}
                  onChange={(codice) => set({ iva_codice: codice || null })}
                  testId="anagrafica-iva-codice"
                  placeholder="Seleziona codice IVA..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  IVA con cui questo fornitore tipicamente fattura
                  {(() => {
                    const cats = summarizeCategorie(a.categorie).filter(
                      (c) => c.key !== "base" && c.key !== "utenti_interni"
                    );
                    if (!cats.length) return null;
                    return (
                      <span className="text-slate-400">
                        {" · Categoria: "}
                        <span className="font-mono">
                          {cats.map((c) => c.label).join(", ")}
                        </span>
                      </span>
                    );
                  })()}
                </p>
                {a._data_quality?.iva_da_codificare && (
                  <div className="text-xs text-amber-700 mt-1" data-testid="anagrafica-iva-da-codificare-banner">
                    ⚠️ Codice IVA da codificare (migrato automaticamente, richiede review)
                  </div>
                )}
              </div>
              <div><Label className="text-xs">Pagamento predefinito</Label>
                <Input value={a.pagamento_predefinito || ""} onChange={(e) => set({ pagamento_predefinito: e.target.value })} /></div>
              <div><Label className="text-xs">Sconto fisso (%)</Label>
                <Input type="number" value={a.sconto_fisso ?? ""} onChange={(e) => set({ sconto_fisso: e.target.value === "" ? null : Number(e.target.value) })} /></div>
              <div><Label className="text-xs">Fatturato annuo</Label>
                <Input type="number" value={a.fatturato_annuo ?? ""} onChange={(e) => set({ fatturato_annuo: e.target.value === "" ? null : Number(e.target.value) })} /></div>
              <div><Label className="text-xs">N. dipendenti</Label>
                <Input type="number" value={a.numero_dipendenti ?? ""} onChange={(e) => set({ numero_dipendenti: e.target.value === "" ? null : Number(e.target.value) })} /></div>
            </div>
          </Card>

          {/* Sprint 4F2-CVT.E — Default CVT (visibili solo per broker assicurativi) */}
          {(a.categorie?.fornitore_servizi_finanziari || []).includes("assicurazione") && (
            <Card className="p-6 border border-slate-200 shadow-sm bg-white mt-3"
                  data-testid="anagrafica-default-cvt">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Default CVT (Assicurazione)</h3>
                  <p className="text-xs text-slate-500">
                    Configurazione applicata di default alle nuove polizze CVT con questo broker.
                  </p>
                </div>
              </div>
              {(a.cvt_default_ribaltamento_fattura == null
                || a.cvt_default_ra_a_carico_concessionaria == null) && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200
                                 p-2 rounded mb-2"
                     data-testid="cvt-defaults-warning">
                  ⚠ Configura questi default per poter creare polizze CVT con questo broker.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Fatturazione CVT
                  </Label>
                  <div className="space-y-1 mt-1" data-testid="cvt-anag-ribaltamento">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="radio"
                             checked={a.cvt_default_ribaltamento_fattura === false}
                             onChange={() => set({ cvt_default_ribaltamento_fattura: false })}
                             data-testid="cvt-anag-rib-false" />
                      Broker fattura direttamente cliente
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="radio"
                             checked={a.cvt_default_ribaltamento_fattura === true}
                             onChange={() => set({ cvt_default_ribaltamento_fattura: true })}
                             data-testid="cvt-anag-rib-true" />
                      Ribaltamento concessionaria
                    </label>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Ritenuta acconto
                  </Label>
                  <div className="space-y-1 mt-1" data-testid="cvt-anag-ra">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="radio"
                             checked={a.cvt_default_ra_a_carico_concessionaria === true}
                             onChange={() => set({ cvt_default_ra_a_carico_concessionaria: true })}
                             data-testid="cvt-anag-ra-conc" />
                      A carico concessionaria (RA è costo)
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="radio"
                             checked={a.cvt_default_ra_a_carico_concessionaria === false}
                             onChange={() => set({ cvt_default_ra_a_carico_concessionaria: false })}
                             data-testid="cvt-anag-ra-broker" />
                      A carico broker (RA è credito d'imposta, neutra)
                    </label>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="fatt_e" className="mt-4">
          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Codice SDI</Label>
                <Input value={a.cod_sdi || ""} onChange={(e) => set({ cod_sdi: e.target.value.toUpperCase() })}
                       className="font-mono uppercase" /></div>
              <div><Label className="text-xs">Riferimento amministrativo</Label>
                <Input value={a.riferimento_amministrativo || ""} onChange={(e) => set({ riferimento_amministrativo: e.target.value })} /></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="documenti" className="mt-4">
          <DocumentsTab ownerType="anagrafica" ownerId={a.id} companyId={null}
                        canUpload={true} canDelete={elev} />
        </TabsContent>

        <TabsContent value="veicoli" className="mt-4">
          <Card className="p-4 border border-slate-200 shadow-sm bg-white">
            {veicoli.length === 0 ? (
              <div className="text-center py-8 text-slate-400 italic">Nessun veicolo associato</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b text-left text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Codice</th>
                      <th className="px-3 py-2">Marca / Modello</th>
                      <th className="px-3 py-2">Targa</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Ruoli</th>
                      <th className="px-3 py-2">Stato vendita</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {veicoli.map((v) => {
                      // Sprint 4A.5 P1: backend fornisce array `ruoli`
                      const ruoli = v.ruoli || [];
                      const ruoloColor = {
                        fornitore: "bg-amber-50 text-amber-700 border-amber-100",
                        cointestatario: "bg-violet-50 text-violet-700 border-violet-100",
                        cliente: "bg-emerald-50 text-emerald-700 border-emerald-100",
                      };
                      const ruoloLabel = {
                        fornitore: "Fornitore",
                        cointestatario: "Cointestatario",
                        cliente: "Cliente",
                      };
                      return (
                        <tr key={v.id} className="hover:bg-slate-50" data-testid={`anag-veicolo-row-${v.id}`}>
                          <td className="px-3 py-2 font-mono text-xs">{v.codice}</td>
                          <td className="px-3 py-2"><Car className="inline h-3 w-3 mr-1 text-slate-400" />{v.marca} {v.modello}</td>
                          <td className="px-3 py-2 font-mono text-xs">{v.targa || "—"}</td>
                          <td className="px-3 py-2 text-xs">{v.status}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {ruoli.length === 0 ? <span className="text-slate-400">—</span> : ruoli.map((r) => (
                                <span
                                  key={r}
                                  className={`text-[10px] px-1.5 py-0.5 rounded border ${ruoloColor[r] || "bg-slate-50 text-slate-700 border-slate-200"}`}
                                  data-testid={`anag-veicolo-ruolo-${r}`}
                                >
                                  {ruoloLabel[r] || r}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {v.vendita_status ? (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] capitalize">
                                {v.vendita_status}
                              </span>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Link to={`/vehicles/${v.id}`} className="text-blue-600 hover:underline text-xs flex items-center justify-end gap-1">
                              Apri <ExternalLink className="h-3 w-3" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="pratiche" className="mt-4">
          <Card className="p-12 text-center bg-slate-50 border-dashed">
            <div className="text-slate-700 font-medium">Pratiche associate</div>
            <div className="text-slate-500 text-sm mt-1">Disponibile da Sprint 4</div>
          </Card>
        </TabsContent>

        {elev && (
          <TabsContent value="storico" className="mt-4">
            <Card className="p-4">
              {audit.length === 0 ? (
                <div className="text-center text-slate-500 py-8">Nessuna voce storico.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {audit.map((a, idx) => (
                    <AuditRowAuto key={idx} entry={a} idx={idx} />
                  ))}
                </ul>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>
      <GuardModal />
      <Conflitti esito={esitoConflitti} mieiValori={mieiValori} onApplica={applicaConflitti}
        onChiudi={() => { setEsitoConflitti(null); toast.message("Tenuti i valori attuali sui campi in conflitto"); }} />


      {/* Sprint 4D (C2): modale duplicate CF su PATCH */}
      {dupModal.open && dupModal.existing && (
        <Dialog open={dupModal.open} onOpenChange={(o) => !o && setDupModal({ open: false, existing: null })}>
          <DialogContent className="max-w-md" data-testid="anag-detail-duplicate-modal">
            <DialogHeader>
              <DialogTitle className="text-amber-700">⚠ Codice fiscale già usato</DialogTitle>
              <DialogDescription>Il CF è già usato da:</DialogDescription>
            </DialogHeader>
            <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200">
              <div className="font-mono text-xs text-amber-700">{dupModal.existing.codice}</div>
              <div className="font-medium text-slate-900">{dupModal.existing.label}</div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDupModal({ open: false, existing: null })}>Annulla</Button>
              <a href={`/anagrafiche/${dupModal.existing.id}`} target="_blank" rel="noreferrer">
                <Button variant="outline">Apri esistente</Button>
              </a>
              <Button onClick={() => save(true)} className="bg-amber-600 hover:bg-amber-700"
                      data-testid="anag-detail-force-create-btn">
                Salva comunque
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

